<script>
/* eslint-disable vue/no-bare-strings-in-template */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import wootConstants from 'dashboard/constants/globals';
import AceleresSessionAPI from 'dashboard/api/aceleresSession';

export default {
  name: 'AceleresWebhookSessionExpiredPanel',
  mixins: [inboxMixin],
  data() {
    return {
      isLoading: null,
      showAgentList: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      inboxById: 'inboxes/getInbox',
      assignableAgents: 'inboxAssignableAgents/getAssignableAgents',
    }),
    conversationId() {
      return this.currentChat?.id;
    },
    inbox() {
      return this.inboxById(this.currentChat?.inbox_id);
    },
    additionalAttributes() {
      return this.inbox?.additional_attributes || {};
    },
    markedOfficial() {
      return (
        this.additionalAttributes.aceleres_official_api === true ||
        this.additionalAttributes.aceleres_official_api === 'true'
      );
    },
    usesAceleresWhatsappWebhook() {
      const webhookUrl = this.inbox?.webhook_url || '';
      return (
        webhookUrl.includes('whatsapp.aceleres.ai') &&
        webhookUrl.includes('/chatwoot/webhook/whatsapp_')
      );
    },
    show() {
      return (
        !!this.conversationId &&
        this.isAPIInbox &&
        !this.markedOfficial &&
        this.usesAceleresWhatsappWebhook &&
        this.currentChat?.can_reply === false
      );
    },
    allowRestart() {
      return (
        this.additionalAttributes.allow_restart_after_expiry === true ||
        this.additionalAttributes.allow_restart_after_expiry === 'true'
      );
    },
    currentAssigneeId() {
      return this.currentChat?.meta?.assignee?.id;
    },
    transferableAgents() {
      return (this.assignableAgents || []).filter(
        agent => agent.id !== this.currentAssigneeId
      );
    },
  },
  watch: {
    conversationId() {
      this.showAgentList = false;
      this.isLoading = null;
    },
  },
  methods: {
    async registerNotice() {
      try {
        await AceleresSessionAPI.registerExpiryNotice(this.conversationId);
      } catch (error) {
        // Best effort: the action must not fail only because the activity note failed.
      }
    },
    async onConcluir() {
      if (this.isLoading) return;
      this.isLoading = 'concluir';
      try {
        await this.registerNotice();
        await this.$store.dispatch('toggleStatus', {
          conversationId: this.conversationId,
          status: wootConstants.STATUS_TYPE.RESOLVED,
        });
        useAlert('Atendimento concluído');
      } catch (error) {
        useAlert('Não foi possível concluir o atendimento');
      } finally {
        this.isLoading = null;
      }
    },
    toggleTransferir() {
      this.showAgentList = !this.showAgentList;
    },
    async onTransferTo(agent) {
      if (this.isLoading) return;
      this.isLoading = 'transferir';
      this.showAgentList = false;
      try {
        await this.registerNotice();
        await this.$store.dispatch('setCurrentChatAssignee', {
          conversationId: this.conversationId,
          assignee: agent,
        });
        await this.$store.dispatch('assignAgent', {
          conversationId: this.conversationId,
          agentId: agent.id,
        });
        useAlert(`Atendimento transferido para ${agent.name}`);
      } catch (error) {
        useAlert('Não foi possível transferir o atendimento');
      } finally {
        this.isLoading = null;
      }
    },
    async onReiniciar() {
      if (this.isLoading) return;
      this.isLoading = 'reiniciar';
      try {
        await AceleresSessionAPI.restartSession(this.conversationId);
        useAlert('Sessão reiniciada. Você tem 24h para responder.');
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            'Reabertura não liberada para esta instância'
        );
      } finally {
        this.isLoading = null;
      }
    },
  },
};
</script>

<template>
  <div v-if="show" class="acl-session-expired">
    <div class="acl-session-expired__top">
      <div class="acl-session-expired__icon" aria-hidden="true">
        <span class="i-lucide-clock-fading" />
      </div>
      <div class="acl-session-expired__text">
        <strong>Atendimento expirado</strong>
        <span>A janela de 24 horas foi encerrada. Escolha uma ação:</span>
      </div>
    </div>

    <div class="acl-session-expired__actions">
      <button
        type="button"
        class="acl-sep-btn acl-sep-btn--primary"
        :disabled="!!isLoading"
        @click="onConcluir"
      >
        Concluir
      </button>
      <button
        type="button"
        class="acl-sep-btn"
        :class="{ 'acl-sep-btn--active': showAgentList }"
        :disabled="!!isLoading"
        @click="toggleTransferir"
      >
        Transferir
      </button>
      <button
        v-if="allowRestart"
        type="button"
        class="acl-sep-btn acl-sep-btn--restart"
        :disabled="!!isLoading"
        @click="onReiniciar"
      >
        Reiniciar
      </button>
      <span v-else class="acl-sep-btn acl-sep-btn--disabled">
        Reiniciar bloqueado
      </span>
    </div>

    <div v-if="showAgentList" class="acl-session-expired__agents">
      <div
        v-if="!transferableAgents.length"
        class="acl-session-expired__agents-empty"
      >
        Nenhum outro agente disponível nesta inbox.
      </div>
      <button
        v-for="agent in transferableAgents"
        :key="agent.id"
        type="button"
        class="acl-agent-row"
        @click="onTransferTo(agent)"
      >
        <span class="acl-agent-row__avatar">
          {{ (agent.name || '?').trim().charAt(0).toUpperCase() }}
        </span>
        <span class="acl-agent-row__name">{{ agent.name }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.acl-session-expired {
  box-sizing: border-box;
  margin: 0.5rem;
  min-height: 0;
  flex: 0 0 auto;
  align-self: stretch;
  padding: 0.75rem 0.875rem;
  border: 1px solid #f1ddca;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, #fffdfa 0%, #fff8ef 100%);
  box-shadow: 0 0.75rem 2rem rgba(104, 74, 45, 0.08);
  color: #454545;
}
.acl-session-expired__top {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.acl-session-expired__icon {
  display: flex;
  width: 2.5rem;
  height: 2.5rem;
  flex: 0 0 2.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #ffeddc;
  color: #c45b16;
}
.acl-session-expired__text {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  color: #68707a;
}
.acl-session-expired__text strong {
  display: block;
  color: #68707a;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1rem;
}
.acl-session-expired__text span {
  margin: 0.425rem 0 0;
  color: #68707a;
  font-size: 13px;
  line-height: 1.125rem;
}
.acl-session-expired__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
.acl-sep-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid #fdba74;
  background: #fff;
  color: #9a3412;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.acl-sep-btn:disabled,
.acl-sep-btn--disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.acl-sep-btn--primary {
  background: #16a34a;
  border-color: #16a34a;
  color: #fff;
}
.acl-sep-btn--restart {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}
.acl-session-expired__agents {
  margin-top: 10px;
  border-top: 1px solid #fed7aa;
  padding-top: 8px;
}
.acl-session-expired__agents-empty {
  font-size: 12px;
  color: #9a3412;
}
.acl-agent-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  border: 0;
  background: transparent;
  color: #7c2d12;
  cursor: pointer;
  text-align: left;
}
.acl-agent-row:hover {
  background: #ffedd5;
}
.acl-agent-row__avatar {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fed7aa;
  font-size: 11px;
  font-weight: 700;
}
</style>
